import React, { useState, useMemo } from 'react';
import investmentData from './investmentData.json';
import PageLayout from './PageLayout';
import './styles/clean.css';
import './styles/investments.css';

const CATEGORY_ICONS = {
  'Provident Fund (PF)': '🏛️',
  'National Pension System (NPS)': '🎯',
  'Mutual Funds (MF)': '📊',
  'Exchange Traded Funds (ETFs)': '📉',
  'Systematic Investment Plans (SIPs)': '🔄',
  'Fixed Income Options': '🔒',
  'Direct Equity Investments': '📈',
  'Smallcase (Curated stock portfolios)': '🗂️',
  'Gold-related Investments': '🥇',
  'Unit Linked Insurance Plans (ULIPs)': '🛡️',
  'Alternative Investments': '💎',
  'Futures & Options (Derivatives)': '⚡',
  'Physical Assets': '🏠',
};

const QUICK_STATS = [
  { label: 'EPF / PPF', value: '7.1–8.25%', sub: 'Govt-backed, tax-free', color: 'green' },
  { label: 'NPS', value: '8–15%', sub: 'Market-linked pension', color: 'blue' },
  { label: 'Mutual Funds', value: '12–18%', sub: 'Equity CAGR (hist.)', color: 'blue' },
  { label: 'FD Rates', value: '6–9%', sub: 'Top banks, 1–5yr', color: 'gray' },
  { label: 'Gold ETF', value: '10–12%', sub: '10yr CAGR historical', color: 'yellow' },
  { label: 'Direct Equity', value: '15–20%', sub: 'Long-term Nifty/Sensex', color: 'green' },
];

const FILTER_PRESETS = [
  { id: 'all', label: 'All', description: 'Show every investment category.' },
  { id: 'beginner', label: 'Beginner friendly', description: 'Lower complexity, diversified, or familiar options.' },
  { id: 'tax', label: 'Tax saving', description: 'Options with 80C or other tax-benefit language.' },
  { id: 'cash', label: 'Parking cash', description: 'Liquid, low-volatility, or fixed-income options.' },
  { id: 'wealth', label: 'Long-term wealth', description: 'Equity or growth-oriented options for longer horizons.' },
  { id: 'lowRisk', label: 'Low risk', description: 'Low volatility, fixed, or government-backed options.' },
  { id: 'sip', label: 'SIP-friendly', description: 'Options commonly used for regular investing.' },
];

const GLOSSARY = [
  { term: 'CAGR', text: 'Annualized growth rate over multiple years.' },
  { term: 'LTCG', text: 'Long-term capital gains tax on assets held beyond the specified period.' },
  { term: 'STCG', text: 'Short-term capital gains tax on assets sold before the long-term threshold.' },
  { term: 'Lock-in', text: 'Period when withdrawal is restricted or penalized.' },
  { term: 'Liquidity', text: 'How quickly you can access money without major cost or delay.' },
  { term: 'Expense ratio', text: 'Annual fund-management cost deducted from returns.' },
];

const renderValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return (
        <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
          {value.map((item, idx) => <li key={idx}>{renderValue(item)}</li>)}
        </ul>
      );
    }
    return (
      <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
        {Object.entries(value).map(([k, v]) => (
          <li key={k}><strong>{k}:</strong> {renderValue(v)}</li>
        ))}
      </ul>
    );
  }
  return String(value);
};

const getTextBlob = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(getTextBlob).join(' ');
  if (typeof value === 'object' && value !== null) return Object.values(value).map(getTextBlob).join(' ');
  return String(value || '');
};

const getDecisionProfile = (name, item) => {
  const text = `${name} ${getTextBlob(item)}`.toLowerCase();
  const risk = /very low|none|fixed|government|safe|provident|ppf|post office|fd|deposit|savings|nsc|scss|ssy/.test(text)
    ? 'Low'
    : /moderate|hybrid|balanced|debt|bond|gold|etf|nps/.test(text)
      ? 'Moderate'
      : /derivative|futures|options|sector|thematic|direct equity|stock|aif|crypto|high/.test(text)
        ? 'High'
        : 'Medium';
  const liquidity = /no lock|high liquidity|savings|liquid fund|etf|direct equity|stocks/.test(text)
    ? 'High'
    : /15 years|retirement|21 years|lock|until age|5 years|3-year|long lock/.test(text)
      ? 'Low'
      : 'Medium';
  const horizon = /retirement|15 years|21 years|5\+ years|long-term|long term|equity|wealth/.test(text)
    ? 'Long'
    : /short|liquid|savings|7 days|none|cash/.test(text)
      ? 'Short'
      : 'Medium';
  const tax = /80c|tax-deductible|tax benefit|tax-free|exempt/.test(text)
    ? 'Tax-advantaged'
    : /taxable|capital gains|slab/.test(text)
      ? 'Taxable'
      : 'Check tax rules';
  const goodFor = risk === 'Low'
    ? 'Capital safety or predictable savings'
    : horizon === 'Long'
      ? 'Long-term wealth building'
      : 'Diversification or specific exposure';
  const avoidWhen = liquidity === 'Low'
    ? 'Avoid if you may need money soon'
    : risk === 'High'
      ? 'Avoid for short horizons or low risk tolerance'
      : 'Avoid if tax/return assumptions do not fit your case';
  return { risk, liquidity, horizon, tax, goodFor, avoidWhen };
};

const getCompareId = (categoryKey, itemName) => `${categoryKey}::${itemName}`;

const DetailPanel = ({ categoryKey, itemName, item, isCompared, onToggleCompare, compareDisabled }) => {
  const profile = getDecisionProfile(itemName, item);
  return (
    <div className="invest-detail-panel">
      <div className="invest-decision-card">
        <div>
          <span>Good for</span>
          <strong>{profile.goodFor}</strong>
        </div>
        <div>
          <span>Avoid when</span>
          <strong>{profile.avoidWhen}</strong>
        </div>
        <div className="invest-decision-badges">
          <span>Risk: {profile.risk}</span>
          <span>Liquidity: {profile.liquidity}</span>
          <span>Horizon: {profile.horizon}</span>
          <span>{profile.tax}</span>
        </div>
        <button
          type="button"
          className={`invest-compare-btn ${isCompared ? 'invest-compare-btn--active' : ''}`}
          onClick={() => onToggleCompare(categoryKey, itemName, item)}
          disabled={!isCompared && compareDisabled}
          title={isCompared ? 'Remove this option from the comparison tray.' : 'Add this option to the comparison tray.'}
        >
          {isCompared ? 'In comparison' : 'Compare'}
        </button>
      </div>
      {Object.entries(item).map(([key, value]) => (
        <div key={key} className="invest-detail-row">
          <span className="invest-detail-key">{key}</span>
          <span className="invest-detail-val">{renderValue(value)}</span>
        </div>
      ))}
    </div>
  );
};

const SubCategoryList = ({ category, categoryKey, openStates, toggleState, compareIds, onToggleCompare, compareDisabled }) => {
  if (!category.subCategories && typeof category === 'object') {
    return (
      <DetailPanel
        categoryKey={categoryKey}
        itemName={categoryKey}
        item={category}
        isCompared={compareIds.includes(getCompareId(categoryKey, categoryKey))}
        onToggleCompare={onToggleCompare}
        compareDisabled={compareDisabled}
      />
    );
  }
  if (!category.subCategories) return null;

  return (
    <div className="invest-subcategory-list">
      {Object.entries(category.subCategories).map(([key, subCategory]) => (
        <div key={key} className="invest-subcategory-item">
          <button className="invest-sub-btn" onClick={() => toggleState(categoryKey, key)} title={`Open or close details for ${key}.`}>
            <span className="invest-category-arrow">
              {openStates[categoryKey]?.[key] ? '▼' : '▶'}
            </span>
            {key}
          </button>
          {openStates[categoryKey]?.[key] && (
            <DetailPanel
              categoryKey={categoryKey}
              itemName={key}
              item={subCategory}
              isCompared={compareIds.includes(getCompareId(categoryKey, key))}
              onToggleCompare={onToggleCompare}
              compareDisabled={compareDisabled}
            />
          )}
        </div>
      ))}
    </div>
  );
};

const matchesSearch = (key, category, query) => {
  const q = query.toLowerCase();
  if (key.toLowerCase().includes(q)) return true;
  if (!category.subCategories) return false;
  return Object.keys(category.subCategories).some(sub => sub.toLowerCase().includes(q));
};

const matchesFilter = (key, category, filter) => {
  if (filter === 'all') return true;
  const text = `${key} ${getTextBlob(category)}`.toLowerCase();
  if (filter === 'beginner') return /ppf|provident|index|large cap|balanced|fd|savings|post office|liquid/.test(text);
  if (filter === 'tax') return /80c|tax benefit|tax-deductible|tax-free|elss|ppf|nps|provident|nsc|ssy/.test(text);
  if (filter === 'cash') return /savings|fixed deposit|fd|liquid|debt|post office|recurring|short-term|low volatility/.test(text);
  if (filter === 'wealth') return /equity|mutual fund|index|large cap|multi-cap|direct equity|stocks|nps|long-term|wealth/.test(text);
  if (filter === 'lowRisk') return /very low|low|fixed|safe|government|provident|ppf|fd|deposit|post office|nsc|scss|ssy/.test(text);
  if (filter === 'sip') return /sip|mutual fund|index|equity|nps|ppf|systematic/.test(text);
  return true;
};

const ComparePanel = ({ items, onRemove, onClear }) => {
  if (items.length === 0) return null;
  return (
    <section className="invest-compare-panel" aria-label="Investment comparison">
      <div className="invest-compare-head">
        <div>
          <strong>Comparison tray</strong>
          <p>Compare 2-3 options using the same plain-English decision fields.</p>
        </div>
        <button type="button" className="invest-action-btn" onClick={onClear}>Clear comparison</button>
      </div>
      <div className="invest-compare-grid">
        {items.map(item => {
          const profile = getDecisionProfile(item.itemName, item.item);
          return (
            <article className="invest-compare-card" key={item.id}>
              <div className="invest-compare-card-head">
                <div>
                  <strong>{item.itemName}</strong>
                  <span>{item.categoryKey}</span>
                </div>
                <button type="button" className="invest-action-btn" onClick={() => onRemove(item.id)}>Remove</button>
              </div>
              <dl className="invest-compare-metrics">
                <div><dt>Risk</dt><dd>{profile.risk}</dd></div>
                <div><dt>Liquidity</dt><dd>{profile.liquidity}</dd></div>
                <div><dt>Horizon</dt><dd>{profile.horizon}</dd></div>
                <div><dt>Tax</dt><dd>{profile.tax}</dd></div>
              </dl>
              <p><b>Good for:</b> {profile.goodFor}</p>
              <p><b>Avoid when:</b> {profile.avoidWhen}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
};

const InvestmentPage = () => {
  const [openStates, setOpenStates] = useState({});
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [compareItems, setCompareItems] = useState([]);

  const toggleState = (categoryKey, subCategoryKey = null) => {
    setOpenStates(prev => {
      const newState = { ...prev };
      if (subCategoryKey === null) {
        newState[categoryKey] = !prev[categoryKey];
      } else {
        if (!newState[categoryKey]) newState[categoryKey] = {};
        newState[categoryKey] = {
          ...newState[categoryKey],
          [subCategoryKey]: !prev[categoryKey]?.[subCategoryKey],
        };
      }
      return newState;
    });
  };

  const expandAll = () => {
    const all = {};
    Object.entries(investmentData).forEach(([key, cat]) => {
      all[key] = true;
      if (cat.subCategories) {
        all[key] = {};
        Object.keys(cat.subCategories).forEach(sub => { all[key][sub] = true; });
      }
    });
    setOpenStates(all);
  };

  const collapseAll = () => setOpenStates({});

  const filteredData = useMemo(() => {
    return Object.entries(investmentData).filter(([key, cat]) =>
      matchesFilter(key, cat, activeFilter) && (!search.trim() || matchesSearch(key, cat, search))
    );
  }, [search, activeFilter]);

  const totalCategories = Object.keys(investmentData).length;
  const openCount = Object.keys(openStates).filter(k => openStates[k] === true || (typeof openStates[k] === 'object' && openStates[k] !== null)).length;
  const isFiltered = Boolean(search.trim()) || activeFilter !== 'all';
  const compareIds = compareItems.map(item => item.id);
  const toggleCompare = (categoryKey, itemName, item) => {
    const id = getCompareId(categoryKey, itemName);
    setCompareItems(prev => {
      if (prev.some(compareItem => compareItem.id === id)) {
        return prev.filter(compareItem => compareItem.id !== id);
      }
      if (prev.length >= 3) return prev;
      return [...prev, { id, categoryKey, itemName, item }];
    });
  };

  return (
    <PageLayout
      title="Investment Cheatsheet"
      icon="📈"
      subtitle="Reference guide for Indian investment options — click any category to expand"
    >
      <div className="invest-page">
        {/* Quick stats overview */}
        <div className="invest-stats-grid">
          {QUICK_STATS.map(stat => (
            <div key={stat.label} className={`invest-stat-card invest-stat-card--${stat.color}`}>
              <span className="invest-stat-label">{stat.label}</span>
              <span className="invest-stat-value">{stat.value}</span>
              <span className="invest-stat-sub">{stat.sub}</span>
            </div>
          ))}
        </div>

        {/* Search + controls */}
        <div className="invest-toolbar">
          <div className="invest-search-wrap">
            <span className="invest-search-icon">🔍</span>
            <input
              type="text"
              className="invest-search-input"
              placeholder="Search investment types…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              title="Search by investment category or option name."
            />
            {search && (
              <button className="invest-search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>
          <div className="invest-toolbar-actions">
            <button className="invest-action-btn" onClick={expandAll} title="Open every investment category and option.">Expand all</button>
            <button className="invest-action-btn" onClick={collapseAll} title="Close every expanded investment section.">Collapse all</button>
            <span className="invest-toolbar-count">
              {isFiltered ? `${filteredData.length} of ${totalCategories}` : `${totalCategories} categories`}
              {openCount > 0 && ` · ${openCount} open`}
            </span>
          </div>
        </div>

        <div className="invest-filter-panel" aria-label="Guided filters">
          <div>
            <strong>What are you trying to find?</strong>
            <p>Use these presets if the finance categories are unfamiliar.</p>
          </div>
          <div className="invest-filter-chips">
            {FILTER_PRESETS.map(filter => (
              <button
                key={filter.id}
                type="button"
                className={`invest-filter-chip ${activeFilter === filter.id ? 'invest-filter-chip--active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
                title={filter.description}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <details className="invest-help-details">
          <summary>What do these controls mean?</summary>
          <dl>
            <div><dt>Guided filters</dt><dd>Start from intent, like tax saving or parking cash, instead of finance category names.</dd></div>
            <div><dt>Expand</dt><dd>Open a category to see options and their decision snapshot.</dd></div>
            <div><dt>Compare</dt><dd>Add 2-3 options to the comparison tray for risk, liquidity, horizon, and tax side-by-side.</dd></div>
            <div><dt>Glossary</dt><dd>Use the bottom glossary when a finance term is unfamiliar.</dd></div>
          </dl>
        </details>

        <ComparePanel
          items={compareItems}
          onRemove={(id) => setCompareItems(prev => prev.filter(item => item.id !== id))}
          onClear={() => setCompareItems([])}
        />

        {/* Category list */}
        <div className="invest-categories">
          {filteredData.length === 0 ? (
            <div className="invest-empty">
              No results for "<strong>{search}</strong>". Try a broader term.
            </div>
          ) : (
            filteredData.map(([key, category]) => {
              const isOpen = !!openStates[key];
              const icon = CATEGORY_ICONS[key] || '📁';
              const subCount = category.subCategories ? Object.keys(category.subCategories).length : 0;
              return (
                <div key={key} className={`invest-category-block ${isOpen ? 'invest-category-open' : ''}`}>
                  <button className="invest-category-btn" onClick={() => toggleState(key)} title={`Open or close ${key}.`}>
                    <span className="invest-category-arrow">{isOpen ? '▼' : '▶'}</span>
                    <span className="invest-category-icon">{icon}</span>
                    <span className="invest-category-label">{key}</span>
                    {subCount > 0 && (
                      <span className="invest-category-count">{subCount}</span>
                    )}
                  </button>
                  {isOpen && (
                    <div className="invest-category-content">
                      <SubCategoryList
                        category={category}
                        categoryKey={key}
                        openStates={openStates}
                        toggleState={toggleState}
                        compareIds={compareIds}
                        onToggleCompare={toggleCompare}
                        compareDisabled={compareItems.length >= 3}
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <p className="clean-note" style={{ marginTop: 8 }}>
          For reference only. Returns shown are indicative historical ranges. Always verify current rates and consult a qualified financial advisor before investing.
        </p>

        <div className="invest-glossary" aria-label="Finance glossary">
          <strong>Quick glossary</strong>
          <div className="invest-glossary-list">
            {GLOSSARY.map(entry => (
              <span key={entry.term} title={entry.text}>
                <b>{entry.term}</b>: {entry.text}
              </span>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default InvestmentPage;
